import type { CSSProperties, ReactElement, ReactNode } from "react";
import "./Skeleton.scss";

export type SkeletonVariant = "block" | "circle" | "text";

export type SkeletonBorderRadius = "sm" | "base" | "lg" | "full";

export type SkeletonBorderWeight = "thin" | "base" | "strong";

export interface SkeletonProps {
  variant?: SkeletonVariant;
  width?: string | number;
  height?: string | number;
  borderRadius?: SkeletonBorderRadius;
  borderWeight?: SkeletonBorderWeight;
  className?: string;
}

const buildSkeletonClassName = (
  variant: SkeletonVariant,
  borderRadius: SkeletonBorderRadius,
  borderWeight: SkeletonBorderWeight,
  className?: string,
): string => {
  const classes = [
    "skeleton",
    `skeleton--border-${borderWeight}`,
    `skeleton--${variant}`,
  ];

  if (variant !== "circle") {
    classes.push(`skeleton--radius-${borderRadius}`);
  }

  if (className) {
    classes.push(className);
  }

  return classes.join(" ");
};

const resolveSkeletonStyle = (
  width?: string | number,
  height?: string | number,
): CSSProperties | undefined => {
  if (width == null && height == null) {
    return undefined;
  }

  return {
    ...(width != null
      ? { width: typeof width === "number" ? `${width}px` : width }
      : {}),
    ...(height != null
      ? { height: typeof height === "number" ? `${height}px` : height }
      : {}),
  };
};

/** Outlined placeholder block with optional shimmer animation. */
export function Skeleton({
  variant = "block",
  width,
  height,
  borderRadius = "sm",
  borderWeight = "thin",
  className,
}: SkeletonProps): ReactElement {
  return (
    <span
      className={buildSkeletonClassName(
        variant,
        borderRadius,
        borderWeight,
        className,
      )}
      style={resolveSkeletonStyle(width, height)}
      aria-hidden="true"
    />
  );
}

export interface SkeletonStatusProps {
  label?: string;
  children: ReactNode;
  className?: string;
}

/** Accessible wrapper for composite skeleton layouts. */
export function SkeletonStatus({
  label = "Cargando...",
  children,
  className,
}: SkeletonStatusProps): ReactElement {
  return (
    <div
      className={className ? `skeleton-status ${className}` : "skeleton-status"}
      role="status"
      aria-busy="true"
      aria-label={label}
    >
      {children}
    </div>
  );
}
